import React, { createContext, useCallback, useContext, useMemo, useReducer, useRef } from 'react';

import type { ThinkingStep } from '@Models';
import { appendThinkingStep } from '../../Helpers/Agents/thinkingSteps';

/**
 * Per-conversation runtime state. Lives outside AgentContext so that
 * concurrent streams (e.g. user sending in conversation A and switching to B
 * while it streams) write to their own slot and never leak across views.
 */
export interface ConversationRuntime {
  isThinking: boolean;
  /** Steps of the latest turn; kept after it finishes so its message keeps the panel. */
  thinkingSteps: ThinkingStep[];
  /** Frozen when the first answer token arrives (or when the turn ends without one). */
  thinkingSeconds: number | null;
  /** Interaction the steps belong to. */
  turnTimestamp: string | null;
  isLoading: boolean;
  isUnread: boolean;
  startedAtMs: number | null;
}

const EMPTY_RUNTIME: Readonly<ConversationRuntime> = Object.freeze({
  isThinking: false,
  thinkingSteps: Object.freeze([]) as readonly ThinkingStep[] as ThinkingStep[],
  thinkingSeconds: null,
  turnTimestamp: null,
  isLoading: false,
  isUnread: false,
  startedAtMs: null,
});

const toSeconds = (startedAtMs: number | null, atMs: number): number | null =>
  startedAtMs === null ? null : Math.max(1, Math.round((atMs - startedAtMs) / 1000));

export interface RuntimeState {
  byId: Record<string, ConversationRuntime>;
  // Separate refs so membership-only consumers don't re-render on every trace event.
  loadingIds: Set<string>;
  unreadIds: Set<string>;
}

export const INITIAL_RUNTIME_STATE: RuntimeState = {
  byId: {},
  loadingIds: new Set<string>(),
  unreadIds: new Set<string>(),
};

export type RuntimeAction =
  | { type: 'startSend'; id: string; atMs: number; turnTimestamp: string | null }
  | { type: 'setThinking'; id: string; value: boolean; atMs: number }
  | { type: 'appendStep'; id: string; step: ThinkingStep }
  | { type: 'finishSend'; id: string; markUnread: boolean; atMs: number }
  | { type: 'cancelSend'; id: string }
  | { type: 'markRead'; id: string }
  | { type: 'remove'; id: string };

function getEntry(byId: RuntimeState['byId'], id: string): ConversationRuntime {
  return byId[id] ?? EMPTY_RUNTIME;
}

function isEmptyRuntime(r: ConversationRuntime): boolean {
  return !r.isLoading && !r.isThinking && !r.isUnread && r.thinkingSteps.length === 0 && r.startedAtMs === null;
}

function withoutKey(byId: RuntimeState['byId'], id: string): RuntimeState['byId'] {
  if (!(id in byId)) return byId;
  const next = { ...byId };
  delete next[id];
  return next;
}

/** Applies a new entry or drops the slot if the entry is empty (keeps state lean). */
function applyOrDrop(byId: RuntimeState['byId'], id: string, next: ConversationRuntime): RuntimeState['byId'] {
  if (isEmptyRuntime(next)) return withoutKey(byId, id);
  return { ...byId, [id]: next };
}

/** Returns a new Set with `id` added (or the same Set if already present). */
function setWith(set: Set<string>, id: string): Set<string> {
  if (set.has(id)) return set;
  const next = new Set(set);
  next.add(id);
  return next;
}

/** Returns a new Set with `id` removed (or the same Set if not present). */
function setWithout(set: Set<string>, id: string): Set<string> {
  if (!set.has(id)) return set;
  const next = new Set(set);
  next.delete(id);
  return next;
}

export function runtimeReducer(state: RuntimeState, action: RuntimeAction): RuntimeState {
  switch (action.type) {
    case 'startSend':
      return {
        byId: {
          ...state.byId,
          [action.id]: {
            ...EMPTY_RUNTIME,
            isLoading: true,
            isThinking: true,
            turnTimestamp: action.turnTimestamp,
            startedAtMs: action.atMs,
          },
        },
        loadingIds: setWith(state.loadingIds, action.id),
        // Sending implies engagement → clear any prior unread flag for this id.
        unreadIds: setWithout(state.unreadIds, action.id),
      };
    case 'setThinking': {
      const prev = getEntry(state.byId, action.id);
      if (prev.isThinking === action.value) return state;
      // Leaving thinking freezes the duration; a retract re-enters it and clears it.
      const thinkingSeconds = action.value ? null : toSeconds(prev.startedAtMs, action.atMs);
      return {
        ...state,
        byId: applyOrDrop(state.byId, action.id, { ...prev, isThinking: action.value, thinkingSeconds }),
      };
    }
    case 'appendStep': {
      const prev = getEntry(state.byId, action.id);
      const thinkingSteps = appendThinkingStep(prev.thinkingSteps, action.step);
      if (thinkingSteps === prev.thinkingSteps) return state;
      return { ...state, byId: { ...state.byId, [action.id]: { ...prev, thinkingSteps } } };
    }
    case 'finishSend': {
      const prev = getEntry(state.byId, action.id);
      return {
        byId: applyOrDrop(state.byId, action.id, {
          ...prev,
          isLoading: false,
          isThinking: false,
          thinkingSeconds: prev.thinkingSeconds ?? toSeconds(prev.startedAtMs, action.atMs),
          startedAtMs: null,
          isUnread: action.markUnread,
        }),
        loadingIds: setWithout(state.loadingIds, action.id),
        unreadIds: action.markUnread ? setWith(state.unreadIds, action.id) : setWithout(state.unreadIds, action.id),
      };
    }
    case 'cancelSend':
      return {
        byId: withoutKey(state.byId, action.id),
        loadingIds: setWithout(state.loadingIds, action.id),
        unreadIds: setWithout(state.unreadIds, action.id),
      };
    case 'markRead': {
      const prev = getEntry(state.byId, action.id);
      if (!prev.isUnread) return state;
      return {
        ...state,
        byId: applyOrDrop(state.byId, action.id, { ...prev, isUnread: false }),
        unreadIds: setWithout(state.unreadIds, action.id),
      };
    }
    case 'remove':
      return {
        byId: withoutKey(state.byId, action.id),
        loadingIds: setWithout(state.loadingIds, action.id),
        unreadIds: setWithout(state.unreadIds, action.id),
      };
    default:
      return state;
  }
}

interface AgentRuntimeContextType {
  getRuntime: (id: string | null | undefined) => ConversationRuntime;
  /** Synchronous check — true if there's a controller registered for this id (faster than reading state). */
  isInFlight: (id: string) => boolean;
  startSend: (id: string, prompt: string, turn?: { interactionTimestamp: string }) => AbortSignal;
  setThinking: (id: string, value: boolean) => void;
  appendStep: (id: string, step: ThinkingStep) => void;
  finishSend: (id: string, isCurrentlyActive: boolean) => void;
  cancelSend: (id: string) => string | null;
  markRead: (id: string) => void;
  removeRuntime: (id: string) => void;
  loadingIds: Set<string>;
  unreadIds: Set<string>;
}

const AgentRuntimeContext = createContext<AgentRuntimeContextType | undefined>(undefined);

export const AgentRuntimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(runtimeReducer, INITIAL_RUNTIME_STATE);
  const controllersRef = useRef<Map<string, AbortController>>(new Map());
  // Last prompt per conversation — used to repopulate the input on cancel.
  // Lives in a ref because it doesn't drive re-renders.
  const lastPromptsRef = useRef<Map<string, string>>(new Map());

  const getRuntime = useCallback(
    (id: string | null | undefined): ConversationRuntime => {
      if (!id) return EMPTY_RUNTIME;
      return state.byId[id] ?? EMPTY_RUNTIME;
    },
    [state.byId],
  );

  // Sync read of in-flight controllers — used to dedupe rapid double-sends
  // before React has had a chance to flush the `isLoading` state.
  const isInFlight = useCallback((id: string) => controllersRef.current.has(id), []);

  const startSend = useCallback((id: string, prompt: string, turn?: { interactionTimestamp: string }) => {
    const existing = controllersRef.current.get(id);
    if (existing) existing.abort();
    const controller = new AbortController();
    controllersRef.current.set(id, controller);
    lastPromptsRef.current.set(id, prompt);
    dispatch({
      type: 'startSend',
      id,
      atMs: Date.now(),
      turnTimestamp: turn?.interactionTimestamp ?? null,
    });
    return controller.signal;
  }, []);

  const setThinking = useCallback((id: string, value: boolean) => {
    dispatch({ type: 'setThinking', id, value, atMs: Date.now() });
  }, []);

  const appendStep = useCallback((id: string, step: ThinkingStep) => {
    dispatch({ type: 'appendStep', id, step });
  }, []);

  const finishSend = useCallback((id: string, isCurrentlyActive: boolean) => {
    controllersRef.current.delete(id);
    lastPromptsRef.current.delete(id);
    dispatch({ type: 'finishSend', id, markUnread: !isCurrentlyActive, atMs: Date.now() });
  }, []);

  const cancelSend = useCallback((id: string): string | null => {
    const controller = controllersRef.current.get(id);
    if (controller) controller.abort();
    controllersRef.current.delete(id);
    const prompt = lastPromptsRef.current.get(id) ?? null;
    lastPromptsRef.current.delete(id);
    dispatch({ type: 'cancelSend', id });
    return prompt;
  }, []);

  const markRead = useCallback((id: string) => {
    dispatch({ type: 'markRead', id });
  }, []);

  const removeRuntime = useCallback((id: string) => {
    controllersRef.current.delete(id);
    lastPromptsRef.current.delete(id);
    dispatch({ type: 'remove', id });
  }, []);

  const value = useMemo<AgentRuntimeContextType>(
    () => ({
      getRuntime,
      isInFlight,
      startSend,
      setThinking,
      appendStep,
      finishSend,
      cancelSend,
      markRead,
      removeRuntime,
      loadingIds: state.loadingIds,
      unreadIds: state.unreadIds,
    }),
    [
      getRuntime,
      isInFlight,
      startSend,
      setThinking,
      appendStep,
      finishSend,
      cancelSend,
      markRead,
      removeRuntime,
      state.loadingIds,
      state.unreadIds,
    ],
  );

  return <AgentRuntimeContext.Provider value={value}>{children}</AgentRuntimeContext.Provider>;
};

const useAgentRuntime = (): AgentRuntimeContextType => {
  const ctx = useContext(AgentRuntimeContext);
  if (!ctx) {
    throw new Error('useAgentRuntime must be used within AgentRuntimeProvider');
  }
  return ctx;
};

export default useAgentRuntime;
